import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Link } from '@ff-os/shared-react/components/link/Link';
import { ScrollContainer } from '@ff-os/shared-react/components/scroll-container/ScrollContainer';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { selectAmountInQueueByGroupId, selectCanRemoveGroup, selectIsEmptyGroup } from '../../../_redux-groups';
import { removeGroup } from '../../../thunks/removeGroup';
import { PhotoPrintsGrid } from '../../PhotoPrintsGrid';
import { PhotoPrintMobile } from '../photo-print/PhotoPrintMobile';
import styles from './PhotoPrintsGroupWindowPanelBody.module.scss';

const _PhotoPrintsGroupWindowPanelBody = ({
    isEmpty, groupId,
    canRemove, remove,
    browse, close
}: Props) => {
    const { t } = useTranslation();

    const renderPhoto = useCallback(id => (
        <PhotoPrintMobile photoId={id} onMoveOrCopy={close} />
    ), [close]);

    // Removing a group may cause the window stack to fall out of sync. To prevent this
    // from happening, we close the window first and remove the group as quickly as
    // possible after the next render.
    const ensureWindowClosedBeforeRemove = async () => {
        close();
        requestAnimationFrame(remove);
    };

    return (
        <ScrollContainer maxHeight='100%'>
            {isEmpty ? (
                <Grid direction='column' align='center' fullHeight fullWidth>
                    <Cell fit className={styles.noPhotosCell}>
                        <p className={styles.noPhotos}>{t('photo_prints.no_photos')}</p>
                        {canRemove && (
                            <Link
                                className={styles.remove}
                                onClick={ensureWindowClosedBeforeRemove}
                                style='danger' icon='general-ui-bin'>
                                {t('photo_prints.remove_group')}
                            </Link>
                        )}
                    </Cell>

                    <Cell fit>
                        <Button onClick={browse}>
                            {t('photo_prints.add')}
                        </Button>
                    </Cell>
                </Grid>
            ) : (
                <PhotoPrintsGrid groupId={groupId} renderPhoto={renderPhoto} />
            )}
        </ScrollContainer>
    );
};

type OwnProps = {
    groupId: string;
    browse: () => void;
    close: () => void;
};

type Props = OwnProps & {
    isEmpty: boolean;
    remove: () => void;
    canRemove: boolean;
    amountInQueue: number;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    isEmpty: selectIsEmptyGroup(state, ownProps.groupId),
    canRemove: selectCanRemoveGroup(state, ownProps.groupId),
    amountInQueue: selectAmountInQueueByGroupId(state, ownProps.groupId)!
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    remove: () => dispatch(removeGroup(ownProps.groupId))
});

export const PhotoPrintsGroupWindowPanelBody = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsGroupWindowPanelBody);